import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createTournament } from "./api/tournamentsApi";
import { sanitizeApiMessage } from "./auth/clientAuth";
import LocationMapPicker from "./components/LocationMapPicker";
import { DEFAULT_SPORT, SPORTS } from "./sportsOptions";

function CreateTournament() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    sportType: DEFAULT_SPORT,
    date: "",
    location: "",
    latitude: null,
    longitude: null,
    placeId: "",
    entryFee: "",
    teamSlots: "",
    prizeFirst: "",
    prizeSecond: "",
    prizeThird: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onLocationChange = useCallback((next) => {
    setForm((prev) => ({
      ...prev,
      ...(next.lat !== undefined
        ? { latitude: next.lat === null || Number.isNaN(next.lat) ? null : next.lat }
        : {}),
      ...(next.lng !== undefined
        ? { longitude: next.lng === null || Number.isNaN(next.lng) ? null : next.lng }
        : {}),
      ...(next.address !== undefined && next.address !== ""
        ? { location: next.address }
        : {}),
      ...(next.placeId !== undefined ? { placeId: next.placeId } : {})
    }));
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const lat = form.latitude;
    const lng = form.longitude;
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      setError("Pick the venue on the map (or enter latitude and longitude in the fallback fields).");
      return;
    }
    if (!form.location.trim()) {
      setError("Enter a venue name or choose a place on the map.");
      return;
    }

    const fee = Number.parseFloat(String(form.entryFee).trim());
    if (!Number.isFinite(fee) || fee < 0) {
      setError("Enter a valid entry fee (0 for free).");
      return;
    }

    const teams = Number.parseInt(String(form.teamSlots).trim(), 10);
    if (!Number.isFinite(teams) || teams < 1 || teams > 9999) {
      setError("Enter team slots between 1 and 9999.");
      return;
    }

    const p1 = String(form.prizeFirst).trim();
    const p2 = String(form.prizeSecond).trim();
    if (!p1 || !p2) {
      setError("Enter 1st and 2nd prize amounts (use 0 or TBD if not decided).");
      return;
    }

    const p3 = String(form.prizeThird).trim();
    const body = {
      name: form.name.trim(),
      sportType: form.sportType,
      tournamentDate: `${form.date}T12:00:00`,
      location: form.location.trim(),
      latitude: lat,
      longitude: lng,
      placeId: form.placeId || "",
      entryFee: fee,
      teamSlots: teams,
      prizeFirst: p1,
      prizeSecond: p2,
      prizeThird: p3 || null
    };

    setLoading(true);
    try {
      const { response, data } = await createTournament(body);

      if (response.status === 401) {
        setError("Session expired. Log in again.");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        const raw =
          typeof data === "object" && data !== null && "message" in data
            ? data.message
            : "Could not create tournament.";
        setError(sanitizeApiMessage(raw));
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError("Network error. Is the API running?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page">
      <section className="panel form-panel">
        <h1>Post a tournament</h1>
        <p className="muted">
          Tournaments are saved on the server (not in your browser).{" "}
          <Link to="/tournaments">Browse tournaments</Link>
        </p>
        <form onSubmit={onSubmit}>
          <label>Tournament name</label>
          <input value={form.name} onChange={(e) => setField("name", e.target.value)} required />

          <label>Sport type</label>
          <select value={form.sportType} onChange={(e) => setField("sportType", e.target.value)}>
            {SPORTS.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </select>

          <label>Date</label>
          <input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} required />

          <label>Venue / location name</label>
          <input
            value={form.location}
            onChange={(e) => setField("location", e.target.value)}
            placeholder="Venue name or address"
            required
          />

          <label className="map-picker-section-label">Venue on Google Maps</label>
          <LocationMapPicker
            latitude={form.latitude}
            longitude={form.longitude}
            venueLabel={form.location}
            onLocationChange={onLocationChange}
          />

          <label>Entry fee (INR)</label>
          <input
            type="number"
            min="0"
            step="1"
            value={form.entryFee}
            onChange={(e) => setField("entryFee", e.target.value)}
            placeholder="e.g. 500 (0 for free)"
            required
          />

          <label>Number of team slots</label>
          <input
            type="number"
            min="1"
            max="9999"
            value={form.teamSlots}
            onChange={(e) => setField("teamSlots", e.target.value)}
            placeholder="e.g. 16"
            required
          />

          <label>1st prize (amount or text)</label>
          <input
            value={form.prizeFirst}
            onChange={(e) => setField("prizeFirst", e.target.value)}
            placeholder="e.g. 50000 or TBD"
            required
          />

          <label>2nd prize (amount or text)</label>
          <input
            value={form.prizeSecond}
            onChange={(e) => setField("prizeSecond", e.target.value)}
            placeholder="e.g. 25000"
            required
          />

          <label>3rd prize (optional)</label>
          <input
            value={form.prizeThird}
            onChange={(e) => setField("prizeThird", e.target.value)}
            placeholder="Leave blank if none"
          />

          {error ? <p className="error-text">{error}</p> : null}

          <button className="primary-btn" type="submit" disabled={submitted || loading}>
            {loading ? "Posting…" : submitted ? "Posted" : "Post tournament"}
          </button>
        </form>
        {submitted ? (
          <p className="success-text">
            Tournament posted successfully.{" "}
            <Link to="/tournaments">View tournament listing</Link>
          </p>
        ) : null}
      </section>
    </main>
  );
}

export default CreateTournament;
